/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Menu, 
  X, 
  ChevronDown, 
  ChevronRight, 
  Search, 
  Calculator, 
  Percent, 
  TrendingUp, 
  Coins, 
  Layers, 
  ExternalLink,
  ChevronUp,
  Briefcase,
  Ruler,
  Clock,
  Code,
  Home,
  FileText,
  ShieldCheck,
  Scale,
  Mail
} from 'lucide-react';
import { CATEGORIES_DATA } from '../data/calculatorsData';

interface NavbarProps {
  currentTab: string;
  setTab: (tab: any) => void;
}

export function Navbar({ currentTab, setTab }: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [expandedMobileCategories, setExpandedMobileCategories] = useState<Record<string, boolean>>({});
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Close dropdowns on route changes
  useEffect(() => {
    setActiveDropdown(null);
    setMobileMenuOpen(false);
  }, [currentTab]);

  // Expand first couple of categories on mobile by default
  useEffect(() => {
    if (CATEGORIES_DATA.length > 0) {
      setExpandedMobileCategories({
        [CATEGORIES_DATA[0].id]: true,
        [CATEGORIES_DATA[1].id]: true
      });
    }
  }, []);

  const handleDropdownHover = (menuId: string | null) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (menuId) {
      setActiveDropdown(menuId);
    } else {
      timeoutRef.current = setTimeout(() => {
        setActiveDropdown(null);
      }, 150);
    }
  };

  const toggleMobileCategory = (catId: string) => {
    setExpandedMobileCategories(prev => ({
      ...prev,
      [catId]: !prev[catId]
    }));
  };

  // Only categories and calculators that are implemented, sorted alphabetically ascending (a, b, c, d, e, f...)
  const activeCategories = CATEGORIES_DATA.map(cat => ({
    ...cat,
    calculators: cat.calculators.filter(calc => calc.isImplemented)
  })).filter(cat => cat.calculators.length > 0)
    .sort((a, b) => a.title.localeCompare(b.title, 'de', { sensitivity: 'base' }));

  // Find calculators for specific groups
  const arbeitGroup = activeCategories.find(c => c.id === 'arbeit-soziales');
  const steuerGroup = activeCategories.find(c => c.id === 'steuerrechner');
  const finanzenGroup = activeCategories.find(c => c.id === 'geldanlage-sparen');
  const kreditGroup = activeCategories.find(c => c.id === 'kredit-finanzierung');
  const umrechnerGroup = activeCategories.find(c => c.id === 'umrechner');
  const zeitGroup = activeCategories.find(c => c.id === 'zeit-datum');

  // Filtered calculators for mobile quick search (only active ones)
  const filteredAllCalculators = CATEGORIES_DATA.flatMap(cat => 
    cat.calculators.map(calc => ({ ...calc, catTitle: cat.title }))
  ).filter(c => 
    c.isImplemented && (
      c.name.toLowerCase().includes(mobileSearchQuery.toLowerCase()) ||
      c.englishName.toLowerCase().includes(mobileSearchQuery.toLowerCase())
    )
  ).slice(0, 8);

  // Navigate handler that updates HTML5 pathname and closes mobile drawer
  const navigateToCalculator = (path: string) => {
    if (path.startsWith('#group-card-')) {
      const hash = path;
      if (window.location.pathname === '/' || window.location.pathname === '/index.html') {
        window.location.hash = hash;
        // Smooth scroll to it
        const el = document.getElementById(hash.substring(1));
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-4', 'ring-blue-600/30', 'border-blue-500');
          setTimeout(() => {
            el.classList.remove('ring-4', 'ring-blue-600/30', 'border-blue-500');
          }, 2500);
        }
      } else {
        // If not on homepage, go to homepage first, with hash
        window.history.pushState(null, '', '/' + hash);
        window.dispatchEvent(new Event('popstate'));
        // Wait a bit for page to render, then scroll
        setTimeout(() => {
          const el = document.getElementById(hash.substring(1));
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('ring-4', 'ring-blue-600/30', 'border-blue-500');
            setTimeout(() => {
              el.classList.remove('ring-4', 'ring-blue-600/30', 'border-blue-500');
            }, 2500);
          }
        }, 300);
      }
      setMobileMenuOpen(false);
      setActiveDropdown(null);
      return;
    }

    const target = path.startsWith('#') ? path.substring(1) : path;
    const clean = target.startsWith('/') ? target : `/${target}`;
    window.history.pushState(null, '', clean);
    window.dispatchEvent(new Event('popstate'));
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const getCleanPath = (path: string) => {
    const target = path.startsWith('#') ? path.substring(1) : path;
    return target.startsWith('/') ? target : `/${target}`;
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/95 backdrop-blur-md shadow-sm">
      {/* Top blue accent bar */}
      <div className="bg-[#004a99] h-2 w-full"></div>
      
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        
        {/* Logo Container and click back to portal */}
        <a 
          href="/"
          onClick={(e) => {
            if (window.location.pathname === '/' || window.location.pathname === '/index.html') {
              e.preventDefault();
              setTab('');
              navigateToCalculator('/');
            }
          }}
          className="flex items-center space-x-2 text-slate-800 hover:text-slate-900 shrink-0 mr-2 xl:mr-4"
          id="logo-container"
        >
          <div className="bg-[#004a99] text-white px-2 py-1 sm:px-2.5 sm:py-1.5 rounded font-extrabold text-base sm:text-lg leading-none shadow-sm transition hover:bg-blue-800 shrink-0">
            SR
          </div>
          <div className="flex flex-col shrink-0">
            <span className="font-display text-sm font-extrabold tracking-tight text-slate-800 uppercase sm:text-base leading-tight whitespace-nowrap">
              SMART-RECHNR<span className="text-[#004a99]">.DE</span>
            </span>
            <span className="text-[9px] uppercase tracking-widest text-[#004a99] font-bold leading-none whitespace-nowrap">
              Professionelle Online-Rechner
            </span>
          </div>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex lg:items-center lg:space-x-1 xl:space-x-1.5 shrink-0" id="desktop-nav">
          
          {/* 1. Home */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              setTab('');
              navigateToCalculator('/');
            }}
            className="flex items-center space-x-1 px-2.5 py-1.5 text-xs xl:text-[13px] font-semibold text-slate-700 hover:text-[#004a99] hover:bg-slate-50 rounded-lg transition whitespace-nowrap shrink-0"
            title="Startseite - Smart-Rechnr.de"
          >
            <Home className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <span className="whitespace-nowrap">Home</span>
          </a>

          {/* 2. Alle Kategorien */}
          <div 
            className="relative"
            onMouseEnter={() => handleDropdownHover('all')}
            onMouseLeave={() => handleDropdownHover(null)}
          >
            <button className={`flex items-center space-x-1 px-2.5 py-1.5 text-xs xl:text-[13px] font-semibold rounded-lg whitespace-nowrap shrink-0 transition ${
              activeDropdown === 'all' ? 'bg-slate-100 text-[#004a99]' : 'text-slate-700 hover:text-[#004a99] hover:bg-slate-50'
            }`}>
              <Layers className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <span className="whitespace-nowrap">Alle Kategorien</span>
              <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${activeDropdown === 'all' ? 'rotate-180' : ''}`} />
            </button>

            {activeDropdown === 'all' && (
              <div className="absolute left-0 mt-1 w-[560px] bg-white rounded-2xl border border-slate-200 p-4 shadow-2xl grid grid-cols-3 gap-3 z-50 max-h-[480px] overflow-y-auto">
                <div className="col-span-3 border-b border-slate-100 pb-2 mb-1">
                  <span className="text-[10px] font-bold text-[#004a99] uppercase tracking-wider">Themenrechner Übersicht</span>
                </div>
                {activeCategories.map((cat) => (
                  <a
                    key={cat.id}
                    href={`#group-card-${cat.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleDropdownHover(null);
                      navigateToCalculator(`#group-card-${cat.id}`);
                    }}
                    className="flex items-center justify-between text-left p-2 rounded-xl border border-slate-100 hover:border-[#004a99]/20 hover:bg-slate-50 transition"
                  >
                    <div>
                      <div className="font-bold text-xs text-slate-800 leading-tight">{cat.title}</div>
                      <span className="text-[9px] text-[#004a99] font-semibold">{cat.calculators.length} Rechner</span>
                    </div>
                    <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* 3. Für Webmaster (Embedder page) */}
          <a
            href="/rechner-einbinden"
            onClick={(e) => {
              e.preventDefault();
              navigateToCalculator('/rechner-einbinden');
            }}
            className="flex items-center space-x-1.5 px-2.5 xl:px-3 py-1.5 text-xs xl:text-[13px] font-bold text-[#004a99] bg-blue-50/90 hover:bg-blue-100 border border-blue-200/80 rounded-lg transition whitespace-nowrap shrink-0 shadow-2xs hover:scale-[1.02] active:scale-95"
            title="Kostenlose Rechner-Widgets für Webmaster einbinden"
          >
            <Code className="h-3.5 w-3.5 text-[#004a99] shrink-0" />
            <span className="whitespace-nowrap">Für Webmaster</span>
          </a>

          {/* 4. Impressum */}
          <a
            href="/impressum"
            onClick={(e) => {
              e.preventDefault();
              navigateToCalculator('/impressum');
            }}
            className="flex items-center space-x-1.5 px-2 xl:px-2.5 py-1.5 text-xs xl:text-[13px] font-semibold text-slate-600 hover:text-[#004a99] hover:bg-slate-50 rounded-lg transition whitespace-nowrap shrink-0"
            title="Impressum & rechtliche Angaben"
          >
            <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <span>Impressum</span>
          </a>

          {/* 5. Datenschutz */}
          <a
            href="/datenschutz"
            onClick={(e) => {
              e.preventDefault();
              navigateToCalculator('/datenschutz');
            }}
            className="flex items-center space-x-1.5 px-2 xl:px-2.5 py-1.5 text-xs xl:text-[13px] font-semibold text-slate-600 hover:text-[#004a99] hover:bg-slate-50 rounded-lg transition whitespace-nowrap shrink-0"
            title="Datenschutzerklärung"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <span>Datenschutz</span>
          </a>

          {/* 6. Haftungsausschluss */}
          <a
            href="/haftungsausschluss"
            onClick={(e) => {
              e.preventDefault();
              navigateToCalculator('/haftungsausschluss');
            }}
            className="flex items-center space-x-1.5 px-2 xl:px-2.5 py-1.5 text-xs xl:text-[13px] font-semibold text-slate-600 hover:text-[#004a99] hover:bg-slate-50 rounded-lg transition whitespace-nowrap shrink-0"
            title="Haftungsausschluss / Disclaimer"
          >
            <Scale className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <span>Haftung</span>
          </a>

          {/* 7. Kontakt */}
          <a
            href="/kontakt"
            onClick={(e) => {
              e.preventDefault();
              navigateToCalculator('/kontakt');
            }}
            className="flex items-center space-x-1.5 px-2 xl:px-2.5 py-1.5 text-xs xl:text-[13px] font-semibold text-slate-600 hover:text-[#004a99] hover:bg-slate-50 rounded-lg transition whitespace-nowrap shrink-0"
            title="Kontakt & Support"
          >
            <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <span>Kontakt</span>
          </a>

        </nav>

        {/* Right Corner: Mobile toggle */}
        <div className="flex items-center space-x-2 lg:hidden shrink-0">
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition"
            aria-label="Hauptmenü"
            id="mobile-hamburger-btn"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Responsive Mobile Sliders / Drawer Menu Overlay */}
      {mobileMenuOpen && (
        <div className="border-t border-slate-200 bg-slate-50/95 backdrop-blur-md w-full max-h-[85vh] overflow-y-auto lg:hidden" id="mobile-drawer">
          <div className="px-4 py-5 space-y-6">
            
            {/* Quick Mobile Search Input */}
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="h-4 w-4 text-slate-400" />
              </span>
              <input
                type="text"
                placeholder="Rechner in allen Kategorien filtern..."
                value={mobileSearchQuery}
                onChange={(e) => setMobileSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-white text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-slate-400 font-medium"
              />
            </div>

            {/* If Mobile Search Query is active */}
            {mobileSearchQuery.trim() ? (
              <div className="space-y-2">
                <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Suchergebnisse am Handy</div>
                {filteredAllCalculators.length > 0 ? (
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
                    {filteredAllCalculators.map((calc, idx) => {
                      const isImpl = !!calc.isImplemented;
                      return (
                        <a
                          key={idx}
                          href={isImpl ? getCleanPath(calc.path) : '#'}
                          onClick={(e) => {
                            e.preventDefault();
                            navigateToCalculator(calc.path);
                          }}
                          className="w-full text-left px-3 py-3 hover:bg-slate-50 flex items-center justify-between text-xs"
                        >
                          <div>
                            <span className="font-semibold text-slate-800 text-xs block">{calc.name}</span>
                            <span className="text-[10px] text-slate-400">{calc.catTitle}</span>
                          </div>
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        </a>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 italic py-4 text-center">{"Kein Rechner gefunden f\u00FCr \""}{mobileSearchQuery}"</div>
                )}
              </div>
            ) : (
              /* Categories Accordion */
              <div className="space-y-3">
                <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Kategorien & Rechnerliste</div>
                <div className="space-y-2.5">
                  {activeCategories.map((cat) => {
                    const isExpanded = expandedMobileCategories[cat.id];
                    return (
                      <div key={cat.id} className="bg-white border border-slate-150/80 rounded-2xl overflow-hidden shadow-sm">
                        {/* Header bar */}
                        <button
                          onClick={() => toggleMobileCategory(cat.id)}
                          className="w-full flex items-center justify-between px-4 py-3.5 font-bold text-slate-800 bg-slate-50/50 hover:bg-slate-100/50 transition text-xs"
                        >
                          <div className="flex items-center space-x-2">
                            <span className="font-display tracking-tight text-slate-800 text-xs font-bold">{cat.title}</span>
                          </div>
                          {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
                        </button>

                        {/* Dropdown Items list */}
                        {isExpanded && (
                          <div className="p-3 bg-white divide-y divide-slate-150 space-y-1">
                            {cat.calculators.map((calc) => {
                              const isImpl = !!calc.isImplemented;
                              return (
                                <a
                                  key={calc.name}
                                  href={isImpl ? getCleanPath(calc.path) : '#'}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    navigateToCalculator(calc.path);
                                  }}
                                  className="w-full text-left px-2 py-3 hover:bg-slate-50 text-slate-600 hover:text-[#004a99] text-xs font-semibold flex items-center justify-between rounded-lg transition"
                                  style={{ minHeight: '44px' }}
                                >
                                  <span>{calc.name}</span>
                                  <ChevronRight className="h-4 w-4 text-slate-300" />
                                </a>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Quick Links in Mobile Drawer */}
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-200">
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  setMobileMenuOpen(false);
                  setTab('');
                  navigateToCalculator('/');
                }}
                className="flex items-center gap-1.5 p-2 rounded-xl bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:text-[#004a99]"
              >
                <Home className="w-3.5 h-3.5 text-slate-400" />
                <span>Home</span>
              </a>
              <a
                href="/rechner-einbinden"
                onClick={(e) => {
                  e.preventDefault();
                  setMobileMenuOpen(false);
                  navigateToCalculator('/rechner-einbinden');
                }}
                className="flex items-center gap-1.5 p-2 rounded-xl bg-blue-50 border border-blue-200 text-xs font-bold text-[#004a99]"
              >
                <Code className="w-3.5 h-3.5 text-[#004a99]" />
                <span>Für Webmaster</span>
              </a>
              <a
                href="/impressum"
                onClick={(e) => {
                  e.preventDefault();
                  setMobileMenuOpen(false);
                  navigateToCalculator('/impressum');
                }}
                className="flex items-center gap-1.5 p-2 rounded-xl bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:text-[#004a99]"
              >
                <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span>Impressum</span>
              </a>
              <a
                href="/datenschutz"
                onClick={(e) => {
                  e.preventDefault();
                  setMobileMenuOpen(false);
                  navigateToCalculator('/datenschutz');
                }}
                className="flex items-center gap-1.5 p-2 rounded-xl bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:text-[#004a99]"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span>Datenschutz</span>
              </a>
              <a
                href="/haftungsausschluss"
                onClick={(e) => {
                  e.preventDefault();
                  setMobileMenuOpen(false);
                  navigateToCalculator('/haftungsausschluss');
                }}
                className="flex items-center gap-1.5 p-2 rounded-xl bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:text-[#004a99]"
              >
                <Scale className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span>Haftung</span>
              </a>
              <a
                href="/kontakt"
                onClick={(e) => {
                  e.preventDefault();
                  setMobileMenuOpen(false);
                  navigateToCalculator('/kontakt');
                }}
                className="flex items-center gap-1.5 p-2 rounded-xl bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:text-[#004a99]"
              >
                <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span>Kontakt</span>
              </a>
            </div>

            {/* Direct Status Badge */}
            <div className="bg-gradient-to-tr from-[#004a99]/10 to-teal-500/10 rounded-2xl p-4 border border-blue-100 flex items-center justify-between">
              <div>
                <span className="text-[10px] font-extrabold text-[#004a99] uppercase tracking-wider block">smart-rechnr.de</span>
                <span className="text-[11px] font-bold text-slate-600 mt-1 block">{"\u00DCber 200+ offizielle Formulare"}</span>
              </div>
              <span className="text-[10px] font-semibold bg-white border border-slate-200 px-2 py-1 rounded-lg text-slate-500 flex items-center gap-1">
                2026 <ExternalLink className="h-3 w-3 text-slate-400" />
              </span>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
